import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Paperclip, Send } from 'react-feather';
import { useSearchParams } from 'react-router-dom';
import { hirelaneClarificationApi } from '../services/api';
import '../components/Hirelane/clarification/HirelaneClarification.css';
import './HirelaneCandidateClarifyPage.css';
import CandidateEngagementPanel from '../components/CandidateEngagementPanel';

const LS_TOKEN = 'hirelane_clarify_token';
const TEAM_TYPING_STUB_MS = 2000;
const NEAR_BOTTOM_PX = 100;
const TEXTAREA_MAX_HEIGHT_PX = 120;

function toTitleCaseName(name) {
  const s = (name || '').trim();
  if (!s || s.toLowerCase() === 'there') return 'there';
  return s
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatTime24(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch {
    return '';
  }
}

export default function HirelaneCandidateClarifyPage() {
  const [searchParams] = useSearchParams();
  const qToken = (searchParams.get('t') || '').trim();
  const [token, setToken] = useState(() => qToken || (typeof localStorage !== 'undefined' ? localStorage.getItem(LS_TOKEN) || '' : ''));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [teamTyping, setTeamTyping] = useState(false);
  const [heldTeamMessageIds, setHeldTeamMessageIds] = useState(() => new Set());
  const [showNewMessagePill, setShowNewMessagePill] = useState(false);
  const [progressPulseToken, setProgressPulseToken] = useState(0);

  const draftTimer = useRef(null);
  const messagesScrollRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dataRef = useRef(null);
  const isInitialSessionRef = useRef(true);
  const teamTypingTimerRef = useRef(null);
  const pendingTeamReleaseRef = useRef([]);
  const hasInitialScrollRef = useRef(false);
  const readyForScrollUpdatesRef = useRef(false);
  const prevDisplayLenRef = useRef(0);
  const skipNextScrollEffectRef = useRef(false);

  useEffect(() => {
    document.documentElement.classList.add('hirelane-clarif-viewport');
    return () => document.documentElement.classList.remove('hirelane-clarif-viewport');
  }, []);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    if (qToken) {
      setToken(qToken);
      try {
        localStorage.setItem(LS_TOKEN, qToken);
      } catch {
        /* ignore */
      }
    }
  }, [qToken]);

  useEffect(() => {
    isInitialSessionRef.current = true;
    hasInitialScrollRef.current = false;
    readyForScrollUpdatesRef.current = false;
    prevDisplayLenRef.current = 0;
    setHeldTeamMessageIds(new Set());
    setTeamTyping(false);
    setShowNewMessagePill(false);
    if (teamTypingTimerRef.current) {
      window.clearTimeout(teamTypingTimerRef.current);
      teamTypingTimerRef.current = null;
    }
    pendingTeamReleaseRef.current = [];
  }, [token]);

  const isNearBottom = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
  }, []);

  const scrollMessagesToTop = useCallback(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    setShowNewMessagePill(false);
  }, []);

  const scrollMessagesToBottom = useCallback((smooth = true) => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth && !reduced ? 'smooth' : 'auto'
    });
    setShowNewMessagePill(false);
  }, []);

  const adjustTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(TEXTAREA_MAX_HEIGHT_PX, Math.max(24, el.scrollHeight));
    el.style.height = `${next}px`;
  }, []);

  const applySessionPayload = useCallback((out, { fromPoll = false } = {}) => {
    if (!out || out.error) return;

    const prevMessages = dataRef.current?.messages || [];
    const prevIds = new Set(prevMessages.map((m) => m.id));
    const incoming = out.messages || [];
    const newTeamMessages = incoming.filter(
      (m) => m.id != null && !prevIds.has(m.id) && m.sender !== 'candidate'
    );

    const isInitial = isInitialSessionRef.current;
    if (isInitial) {
      isInitialSessionRef.current = false;
      setData(out);
      return;
    }

    if (fromPoll && newTeamMessages.length > 0) {
      if (teamTypingTimerRef.current) {
        window.clearTimeout(teamTypingTimerRef.current);
      }

      const holdIds = new Set(newTeamMessages.map((m) => m.id));
      setHeldTeamMessageIds((prev) => {
        const next = new Set(prev);
        holdIds.forEach((id) => next.add(id));
        return next;
      });
      setTeamTyping(true);
      setData(out);
      pendingTeamReleaseRef.current = newTeamMessages;

      teamTypingTimerRef.current = window.setTimeout(() => {
        teamTypingTimerRef.current = null;
        setTeamTyping(false);
        setHeldTeamMessageIds((prev) => {
          const next = new Set(prev);
          pendingTeamReleaseRef.current.forEach((m) => next.delete(m.id));
          return next;
        });
        pendingTeamReleaseRef.current = [];
      }, TEAM_TYPING_STUB_MS);
      return;
    }

    setData(out);
  }, []);

  const refresh = useCallback(async () => {
    const t = token.trim();
    if (!t) {
      setData(null);
      setError('Missing Clarity Loop link. Open the link from your email, or paste the token below.');
      return;
    }
    try {
      const out = await hirelaneClarificationApi.publicSession(t);
      if (out?.error) {
        setError(out.error);
        setData(null);
        return;
      }
      setError(null);
      applySessionPayload(out);
      try {
        localStorage.setItem(LS_TOKEN, t);
      } catch {
        /* ignore */
      }
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Could not load Clarity Loop';
      setError(msg);
      setData(null);
    }
  }, [token, applySessionPayload]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!token.trim()) return;
    let cancelled = false;
    const id = window.setInterval(() => {
      if (cancelled || document.visibilityState !== 'visible') return;
      hirelaneClarificationApi
        .publicSession(token.trim(), { advance: false })
        .then((out) => {
          if (cancelled || out?.error) return;
          applySessionPayload(out, { fromPoll: true });
        })
        .catch(() => {});
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token, applySessionPayload]);

  const displayMessages = useMemo(() => {
    const all = data?.messages || [];
    if (!heldTeamMessageIds.size) return all;
    return all.filter((m) => !heldTeamMessageIds.has(m.id));
  }, [data?.messages, heldTeamMessageIds]);

  useEffect(() => {
    if (!data) return;

    if (!hasInitialScrollRef.current) {
      if (displayMessages.length === 0) return;
      hasInitialScrollRef.current = true;
      requestAnimationFrame(() => {
        scrollMessagesToTop();
        prevDisplayLenRef.current = displayMessages.length;
        readyForScrollUpdatesRef.current = true;
      });
      return;
    }

    if (!readyForScrollUpdatesRef.current) return;

    if (skipNextScrollEffectRef.current) {
      skipNextScrollEffectRef.current = false;
      prevDisplayLenRef.current = displayMessages.length;
      return;
    }

    if (displayMessages.length <= prevDisplayLenRef.current) return;
    prevDisplayLenRef.current = displayMessages.length;

    requestAnimationFrame(() => {
      if (isNearBottom()) {
        scrollMessagesToBottom();
      } else {
        setShowNewMessagePill(true);
      }
    });
  }, [data, displayMessages, isNearBottom, scrollMessagesToBottom, scrollMessagesToTop]);

  const onMessagesScroll = useCallback(() => {
    if (isNearBottom()) {
      setShowNewMessagePill(false);
    }
  }, [isNearBottom]);

  const onTextChange = (v) => {
    setText(v);
    setDrafting(true);
    if (draftTimer.current) window.clearTimeout(draftTimer.current);
    draftTimer.current = window.setTimeout(() => setDrafting(false), 1200);
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [text, adjustTextareaHeight]);

  const send = async () => {
    const t = token.trim();
    const body = text.trim();
    if (!t || (!body && files.length === 0)) return;
    setSending(true);
    setError(null);
    try {
      const out = await hirelaneClarificationApi.publicMessage(t, body, files);
      if (out?.error) {
        setError(out.error);
        return;
      }
      setText('');
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      skipNextScrollEffectRef.current = true;
      applySessionPayload(out);
      requestAnimationFrame(() => scrollMessagesToBottom());
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Send failed');
    } finally {
      setSending(false);
      setDrafting(false);
      requestAnimationFrame(adjustTextareaHeight);
    }
  };

  const skip = async () => {
    const t = token.trim();
    if (!t) return;
    setSkipping(true);
    setError(null);
    try {
      const out = await hirelaneClarificationApi.publicSkip(t);
      if (out?.error) {
        setError(out.error);
        return;
      }
      applySessionPayload(out);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Skip failed');
    } finally {
      setSkipping(false);
    }
  };

  useEffect(
    () => () => {
      if (teamTypingTimerRef.current) window.clearTimeout(teamTypingTimerRef.current);
      if (draftTimer.current) window.clearTimeout(draftTimer.current);
    },
    []
  );

  const asked = data?.questions?.some((q) => q.status === 'asked');
  const displayName = toTitleCaseName(data?.session?.candidate_first_name || 'there');
  const closed = data?.session?.status === 'closed';

  return (
    <div className="hirelane-clarif-public-page">
      <header className="hirelane-clarif-page-masthead">
        <p className="hirelane-clarif-page-masthead__wordmark">
          <span className="hirelane-clarif-page-masthead__smart">Smart</span>
          <span className="hirelane-clarif-page-masthead__rabbitz">Rabbitz</span>
        </p>
        <span className="hirelane-clarif-page-masthead__divider" aria-hidden="true" />
        <span className="hirelane-clarif-page-masthead__tag">Follow-up · Your application</span>
      </header>

      <hr className="hirelane-clarif-page-rule" />

      <div className="hirelane-clarif-public-layout">
        <div className="hirelane-clarif-chat-column">
          {!data ? (
            <div className="hirelane-clarif-chat-preload">
              <header className="hirelane-clarif-chat-intro">
                <h1 className="hirelane-clarif-chat-intro__title">A few more details</h1>
                <p className="hirelane-clarif-chat-intro__subtitle">
                  {displayName === 'there'
                    ? 'A few short questions before we schedule your interview.'
                    : `Hi ${displayName}, a few short questions before we schedule your interview.`}
                </p>
              </header>
              {!qToken && (
                <div className="hirelane-clarif-token-gate">
                  <label htmlFor="clarif-token-paste">
                    If you saved this device before, we may still have your link. Otherwise paste the token from
                    your email URL.
                  </label>
                  <div className="hirelane-clarif-token-gate__row">
                    <input
                      id="clarif-token-paste"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      placeholder="Token from ?t=…"
                    />
                    <button type="button" onClick={() => refresh()}>
                      Load
                    </button>
                  </div>
                </div>
              )}
              {error && <div className="hirelane-clarif-public-error">{error}</div>}
            </div>
          ) : (
            <>
              <header className="hirelane-clarif-chat-intro">
                <h1 className="hirelane-clarif-chat-intro__title">A few more details</h1>
                <p className="hirelane-clarif-chat-intro__subtitle">
                  {displayName === 'there'
                    ? 'A few short questions before we schedule your interview.'
                    : `Hi ${displayName}, a few short questions before we schedule your interview.`}
                </p>
                <p className="hirelane-clarif-conversation-eyebrow">
                  <span className="hirelane-clarif-conversation-eyebrow__rule" aria-hidden="true" />
                  Conversation
                </p>
              </header>

              {error && <div className="hirelane-clarif-public-error hirelane-clarif-public-error--inline">{error}</div>}

              <div className="hirelane-clarif-messages-wrap">
                <div
                  ref={messagesScrollRef}
                  className="hirelane-clarif-messages-scroll"
                  role="log"
                  aria-live="polite"
                  aria-relevant="additions"
                  onScroll={onMessagesScroll}
                >
                  <div className="hirelane-clarif-messages">
                    {displayMessages.map((m) => {
                      const isCand = m.sender === 'candidate';
                      const timeLabel = formatTime24(m.sent_at);
                      return (
                        <article
                          key={m.id}
                          className={`hirelane-clarif-transcript-msg ${isCand ? 'hirelane-clarif-transcript-msg--you' : 'hirelane-clarif-transcript-msg--team'}`}
                        >
                          <span className="hirelane-clarif-transcript-label">
                            {isCand ? 'You' : 'Hiring team'}
                            {timeLabel ? ` · ${timeLabel}` : ''}
                          </span>
                          {isCand ? (
                            <div className="hirelane-clarif-note">
                              <div className="hirelane-clarif-note__body">
                                {m.body}
                                {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                                  <div className="hirelane-clarif-transcript-attachments">
                                    {m.attachments.map((a, idx) => (
                                      <div key={idx}>
                                        <a href={a.url} target="_blank" rel="noreferrer">
                                          {a.name || 'Attachment'}
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="hirelane-clarif-transcript-body">
                              {m.body}
                              {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                                <div className="hirelane-clarif-transcript-attachments">
                                  {m.attachments.map((a, idx) => (
                                    <div key={idx}>
                                      <a href={a.url} target="_blank" rel="noreferrer">
                                        {a.name || 'Attachment'}
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </article>
                      );
                    })}
                    {closed && (
                      <p className="hirelane-clarif-caught-up">All caught up</p>
                    )}
                    {/* <p className="hirelane-clarif-public-hint">
                      Questions are based on profile fields we already have. If something does not apply, use Skip.
                    </p> */}
                    <div ref={messagesEndRef} className="hirelane-clarif-messages-end" aria-hidden="true" />
                  </div>
                </div>

                {showNewMessagePill && (
                  <button
                    type="button"
                    className="hirelane-clarif-new-message-pill"
                    onClick={() => scrollMessagesToBottom()}
                  >
                    New message
                  </button>
                )}
              </div>

              <div className="hirelane-clarif-input-dock">
                {teamTyping && (
                  <div className="hirelane-clarif-typing" role="status" aria-live="polite">
                    <span className="hirelane-clarif-typing__dots" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </span>
                    <span className="hirelane-clarif-typing__label">Hiring team is typing</span>
                  </div>
                )}

                {!closed && (
                  <>
                    {asked && (
                      <div className="hirelane-clarif-skip-row">
                        {/* <button
                          type="button"
                          className="hirelane-clarif-skip-btn"
                          onClick={skip}
                          disabled={skipping || sending}
                        >
                          {skipping ? 'Skipping…' : 'Skip this question'}
                        </button> */}
                      </div>
                    )}
                    <div className="hirelane-clarif-compose-bar">
                      <label className="hirelane-clarif-attach-icon" title="Attach file">
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept="application/pdf,image/*"
                          onChange={(e) => setFiles(Array.from(e.target.files || []))}
                          disabled={sending}
                        />
                        <Paperclip size={18} strokeWidth={2} aria-hidden />
                        <span className="hirelane-clarif-visually-hidden">Attach file</span>
                      </label>
                      <textarea
                        ref={textareaRef}
                        className="hirelane-clarif-compose-input"
                        value={text}
                        onChange={(e) => onTextChange(e.target.value)}
                        onInput={adjustTextareaHeight}
                        placeholder="Type your reply"
                        disabled={sending}
                        rows={1}
                        aria-label="Your reply"
                      />
                      <button
                        type="button"
                        className="hirelane-clarif-send"
                        onClick={send}
                        disabled={sending || (!text.trim() && files.length === 0)}
                        aria-label={sending ? 'Sending' : 'Send'}
                      >
                        <span className="hirelane-clarif-send__label">{sending ? '…' : 'Send'}</span>
                        <Send size={18} strokeWidth={2} className="hirelane-clarif-send__icon" aria-hidden />
                      </button>
                    </div>
                    {files.length > 0 && (
                      <p className="hirelane-clarif-file-hint">
                        {files.length} file{files.length === 1 ? '' : 's'} selected
                      </p>
                    )}
                    {drafting && !sending && <p className="hirelane-clarif-drafting">Drafting…</p>}
                  </>
                )}

                <p className="hirelane-clarif-secured-footer">Secured by Clarity Loop</p>
              </div>
            </>
          )}
        </div>

        <CandidateEngagementPanel progressPulseToken={progressPulseToken} />
      </div>
    </div>
  );
}
